import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/provider';
import styles from './BackendHealthCheck.module.css';

interface BackendHealthCheckProps {
  children: React.ReactNode;
}

const BackendHealthCheck: React.FC<BackendHealthCheckProps> = ({ children }) => {
  const { t } = useTranslation();
  const isReactSnap = typeof window !== 'undefined' && Boolean((window as any).__REACT_SNAP__);
  const isWebdriver = typeof navigator !== 'undefined' && Boolean((navigator as any).webdriver);
  const isPrerender = isReactSnap || isWebdriver;

  const isPublicExercisesRoute =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/exercises' || window.location.pathname.startsWith('/exercises/'));

  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 30; // 30 retries = ~2.5 minutes (5s intervals)

  const checkHealth = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const healthUrl = apiUrl.replace('/api', '/health');

    try {
      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setIsHealthy(true);
        setIsChecking(false);
        return true;
      }
      return false;
    } catch (error) {
      console.log('Health check failed:', error);
      return false;
    }
  };

  useEffect(() => {
    if (isPrerender) {
      return;
    }

    let intervalId: NodeJS.Timeout;

    const performHealthCheck = async () => {
      const healthy = await checkHealth();
      
      if (!healthy && retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
      } else if (!healthy && retryCount >= MAX_RETRIES) {
        setIsHealthy(false);
        setIsChecking(false);
      }
    };

    // Initial check
    performHealthCheck();

    // Set up interval for retries
    if (isHealthy === null || (isChecking && retryCount < MAX_RETRIES)) {
      intervalId = setInterval(performHealthCheck, 5000); // Check every 5 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [retryCount, isHealthy, isChecking, isPrerender]);

  if (isPrerender) {
    return <>{children}</>;
  }

  // Don't block public exercise pages on backend availability.
  // These pages are meant to be publicly accessible and should work even if the backend is sleeping.
  if (isPublicExercisesRoute) {
    return <>{children}</>;
  }

  // Show loading modal while checking
  if (isHealthy === null || isChecking) {
    return (
      <div className={styles.modalOverlay} data-testid="health-check-modal">
        <div className={styles.modal}>
          <div className={styles.spinner}></div>
          <h2>{t('shared.backendHealth.startingTitle')}</h2>
          <p>{t('shared.backendHealth.startingDescription')}</p>
          <p className={styles.retryInfo}>
            {t('shared.backendHealth.attempt', { current: retryCount + 1, total: MAX_RETRIES })}
          </p>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${((retryCount + 1) / MAX_RETRIES) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error modal if health check failed after max retries
  if (isHealthy === false) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2>{t('shared.backendHealth.unavailableTitle')}</h2>
          <p>{t('shared.backendHealth.unavailableDescription')}</p>
          <p className={styles.errorDetails}>
            {t('shared.backendHealth.unavailableDetails')}
          </p>
          <button 
            className={styles.retryButton}
            onClick={() => {
              setIsHealthy(null);
              setIsChecking(true);
              setRetryCount(0);
            }}
          >
            {t('shared.backendHealth.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Backend is healthy, render children
  return <>{children}</>;
};

export default BackendHealthCheck;
